import React, { Suspense, useCallback, useEffect, useRef, useTransition } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useQueryLoader, usePreloadedQuery } from 'react-relay';

import AvatarSkeleton from '@atlaskit/avatar/skeleton';
import { cssMap } from '@atlaskit/css';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Box } from '@atlaskit/primitives/compiled';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics/analytics-context';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { token } from '@atlaskit/tokens';

import { HierarchyChart, type HierarchyData } from '../HierarchyChart';
import { VerticalChartLine } from '../HierarchyChart/styles';

import InteractiveHierarchyModalQueryNode, {
	type InteractiveHierarchyModalQuery as InteractiveHierarchyModalQueryType,
} from './__generated__/InteractiveHierarchyModalQuery.graphql';

const messages = defineMessages({
	title: {
		id: 'reporting-lines.interactive-hierarchy-modal.title',
		defaultMessage: 'Reporting line',
		description: 'Title for the interactive org chart modal',
	},
});

const skeletonStyles = cssMap({
	cardContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		gap: token('space.100'),
		width: '200px',
		maxWidth: '100%',
	},
	cardContent: {
		flex: '1',
	},
	chartContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		paddingBlock: token('space.300'),
		opacity: token('opacity.loading'),
	},
	peersRow: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(285px, 100%), 1fr))',
		maxWidth: '578px',
		justifyItems: 'center',
		gap: token('space.100'),
		width: '100%',
	},
	pendingChart: {
		opacity: token('opacity.loading'),
		transitionProperty: 'opacity',
		transitionDuration: token('motion.duration.short', '150ms'),
		transitionTimingFunction: token('motion.easing.out.practical', 'ease'),
	},
	readyChart: {
		opacity: 1,
		transitionProperty: 'opacity',
		transitionDuration: token('motion.duration.short', '150ms'),
		transitionTimingFunction: token('motion.easing.out.practical', 'ease'),
	},
	modalBodyPadding: {
		paddingBlock: token('space.100'),
		minHeight: '200px',
	},
});

export interface InteractiveHierarchyModalProps {
	accountId: string;
	orgId?: string;
	siteId: string;
	cloudId: string;
	onClose: () => void;
	testId?: string;
	/** The account ID of the currently logged-in user (viewer). Required to show the profile card on hover. */
	requestingUserId?: string;
}

function UserCardSkeleton() {
	return (
		<Box xcss={skeletonStyles.cardContainer}>
			<AvatarSkeleton size="medium" />
			<Box xcss={skeletonStyles.cardContent}>
				<AvatarSkeleton size="xxsmall" />
			</Box>
		</Box>
	);
}

function HierarchyChartSkeleton() {
	return (
		<Box xcss={skeletonStyles.chartContainer}>
			<UserCardSkeleton />
			<VerticalChartLine />
			<UserCardSkeleton />
			<VerticalChartLine />
			<Box xcss={skeletonStyles.peersRow}>
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
			</Box>
		</Box>
	);
}

type ReportingLineUser = {
	accountIdentifier?: string | null;
	numberOfDirectReports?: number | null;
	numberOfTotalReports?: number | null;
	pii?: {
		name?: string | null;
		picture?: string | null;
		extendedProfile?: { jobTitle?: string | null } | null;
	} | null;
};

function mapUser(userData: ReportingLineUser): HierarchyData['user'] | null {
	if (!userData?.accountIdentifier) {
		return null;
	}
	return {
		accountId: userData.accountIdentifier,
		name: userData.pii?.name ?? '',
		avatarUrl: userData.pii?.picture ?? null,
		jobTitle: userData.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: userData.numberOfDirectReports ?? undefined,
		numberOfTotalReports: userData.numberOfTotalReports ?? undefined,
	};
}

interface HierarchyChartLoaderProps {
	queryRef: Parameters<typeof usePreloadedQuery<InteractiveHierarchyModalQueryType>>[1];
	onNodeClick: (accountId: string) => void;
	cloudId: string;
	selectedAccountId: string;
	requestingUserId?: string;
	orgId?: string;
}

function HierarchyChartLoader({
	queryRef,
	onNodeClick,
	cloudId,
	selectedAccountId,
	requestingUserId,
	orgId,
}: HierarchyChartLoaderProps) {
	const { fireEvent } = useAnalyticsEvents();
	// Dedup on `selectedAccountId` so we re-fire when the modal navigates to a new
	// person (even if both responses succeed), but never twice for the same person.
	const lastFiredAccountIdRef = useRef<string | null>(null);

	const data = usePreloadedQuery<InteractiveHierarchyModalQueryType>(
		graphql`
			query InteractiveHierarchyModalQuery($aaid: String!, $orgId: String!, $siteId: String!) {
				knowledgeDiscovery {
					reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
						@optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on KnowledgeDiscoveryReportingLines {
							user {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							managers {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							reports {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							peers {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const reportingLines = data.knowledgeDiscovery?.reportingLines;
	const isValidPayload =
		!!reportingLines && reportingLines.__typename === 'KnowledgeDiscoveryReportingLines';
	const user = isValidPayload && reportingLines.user ? mapUser(reportingLines.user) : null;
	const isLoaded = isValidPayload && !!user;

	// Fire `succeeded.request` / `failed.request` exactly once per loaded person.
	// Re-fires when the modal navigates to a new person (so consecutive valid
	// navigations are still observed); does not re-fire on rerenders for the same
	// person, regardless of whether the request succeeded or failed.
	useEffect(() => {
		if (lastFiredAccountIdRef.current === selectedAccountId) {
			return;
		}
		lastFiredAccountIdRef.current = selectedAccountId;
		if (isLoaded) {
			fireEvent('operational.interactiveHierarchyModal.succeeded.request', {});
		} else {
			fireEvent('operational.interactiveHierarchyModal.failed.request', {});
		}
	}, [selectedAccountId, isLoaded, fireEvent]);

	if (!isValidPayload || !user) {
		return null;
	}

	const managers = (reportingLines.managers ?? [])
		.map((manager) => mapUser(manager))
		.filter((manager): manager is HierarchyData['user'] => manager !== null);

	const reports =
		reportingLines.reports
			?.map((report) => mapUser(report))
			.filter((report): report is HierarchyData['user'] => report !== null) ?? [];

	const peers =
		reportingLines.peers
			?.map((peer) => mapUser(peer))
			.filter((peer): peer is HierarchyData['user'] => peer !== null) ?? [];

	const hierarchyData: HierarchyData = {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
		peers: peers.length ? peers : undefined,
	};

	return (
		<HierarchyChart
			data={hierarchyData}
			onNodeClick={onNodeClick}
			cloudId={cloudId}
			accountId={selectedAccountId}
			orgId={orgId}
			requestingUserId={requestingUserId}
		/>
	);
}

/**
 * Modal that renders an interactive, navigable org chart for a given user.
 * Clicking any user card re-fetches and re-centres the chart on that person.
 */
export function InteractiveHierarchyModal(
	props: InteractiveHierarchyModalProps,
): React.JSX.Element {
	return (
		<TeamsAppAnalyticsContext data={{ source: 'interactiveHierarchyModal' }}>
			<InteractiveHierarchyModalInner {...props} />
		</TeamsAppAnalyticsContext>
	);
}

function InteractiveHierarchyModalInner({
	accountId,
	orgId,
	siteId,
	cloudId,
	onClose,
	testId = 'interactive-hierarchy-modal',
	requestingUserId,
}: InteractiveHierarchyModalProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const [queryRef, loadQuery] = useQueryLoader<InteractiveHierarchyModalQueryType>(
		InteractiveHierarchyModalQueryNode,
	);
	const [isPending, startTransition] = useTransition();
	const selectedAccountIdRef = useRef(accountId);

	useEffect(() => {
		loadQuery({ aaid: accountId, orgId: orgId ?? '', siteId });
		fireEvent('ui.interactiveHierarchyModal.opened', {});
	}, [loadQuery, fireEvent, accountId, orgId, siteId]);

	const handleNodeClick = useCallback(
		(clickedAccountId: string) => {
			selectedAccountIdRef.current = clickedAccountId;
			startTransition(() => {
				loadQuery({ aaid: clickedAccountId, orgId: orgId ?? '', siteId });
			});
		},
		[loadQuery, orgId, siteId],
	);

	const handleClose = useCallback(() => {
		fireEvent('ui.interactiveHierarchyModal.closed', {});
		onClose();
	}, [fireEvent, onClose]);

	return (
		<ModalDialog onClose={handleClose} width="medium" testId={testId}>
			<ModalHeader hasCloseButton>
				<ModalTitle>{intl.formatMessage(messages.title)}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box xcss={skeletonStyles.modalBodyPadding}>
					<Suspense fallback={<HierarchyChartSkeleton />}>
						<Box xcss={isPending ? skeletonStyles.pendingChart : skeletonStyles.readyChart}>
							{queryRef && (
								<HierarchyChartLoader
									queryRef={queryRef}
									onNodeClick={handleNodeClick}
									cloudId={cloudId}
									selectedAccountId={selectedAccountIdRef.current}
									requestingUserId={requestingUserId}
									orgId={orgId}
								/>
							)}
						</Box>
					</Suspense>
				</Box>
			</ModalBody>
		</ModalDialog>
	);
}
